/**
 * ==========================================================================
 *  App.jsx — Top-Level-Routing + Auth-Gate
 * ==========================================================================
 *  Struktur:
 *    - Öffentliche URLs (kein Login nötig):
 *        /buchen  → BookingWidget (Kunden-Formular)
 *        /passwort-zuruecksetzen?token=... → Reset-Seite
 *    - Geschützte Bereiche (nur eingeloggt):
 *        Persistente Top-Navigation mit 4 Tabs (Berater-Dashboard /
 *        Pflege-Cockpit / Liane Box / Super-Admin). Logo-Klick fuehrt zur
 *        Home-Startseite. Kunden-Widget-Vorschau und Magic-Link-Portal
 *        liegen im Settings-Dropdown (selten genutzt). Default nach
 *        Login: Home-Startseite.
 *
 *  Diese einfache Routing-Lösung kommt ohne react-router aus (ein paar
 *  String-Vergleiche auf window.location.pathname). Wenn das später
 *  komplexer wird, ersetzen wir es durch react-router-dom.
 * ==========================================================================
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, LogOut, Loader2, MoreVertical, UploadCloud, X, Calendar as CalendarIcon, LayoutDashboard, Globe, Link as LinkIcon, ShieldCheck, Clock, Activity, Package, Eye, MapPin, Mail, Search, Plus, Trash2, Grid3x3, Ban } from 'lucide-react';

import BookingWidget from './components/BookingWidget';
import LianeBestellWidget from './components/LianeBestellWidget';
import EingebettetesWidget from './components/EingebettetesWidget';
import SchulungAnfrageWidget from './components/SchulungAnfrageWidget';
import VorbereitungsPortal from './components/Vorbereitungs_Portal';
import BeraterDashboard from './pages/Berater_Dashboard';
import SuperAdmin from './pages/Super_Admin';
import Login from './pages/Login';
import PasswortZuruecksetzen from './pages/PasswortZuruecksetzen';
import Home from './pages/Home';
import PflegeCockpit from './pages/Pflege_Cockpit';
import LianeBox from './pages/Liane_Box';
import MailCenter from './pages/MailCenter';
import Tourplan from './pages/Tourplan';
import FeedbackWidget from './pages/FeedbackWidget';
import FeedbackDemo from './pages/FeedbackDemo';

import { AuthProvider, useAuth } from './context/AuthContext';
import TwoFaEinrichtung from './components/TwoFaEinrichtung';
import { api } from './api';
import { telefonTrifft } from './utils/contact';

// ==========================================================================
// Haupt-App mit AuthProvider drumherum
// ==========================================================================
export default function App() {
  return (
    <AuthProvider>
      <AppInhalt />
    </AuthProvider>
  );
}

// ==========================================================================
// AppInhalt — kennt den Auth-Zustand und rendert entsprechend
// ==========================================================================
function AppInhalt() {
  const { user, loading } = useAuth();
  const pfad = window.location.pathname;

  // Beim App-Start prüfen wir kurz, ob wir noch eingeloggt sind. Während
  // dieser Zeit zeigen wir einen dezenten Lade-Indikator.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // --- Öffentliche Routes (kein Login nötig) ---

  // Passwort-Reset-Link aus E-Mail
  if (pfad.startsWith('/passwort-zuruecksetzen')) {
    return <PasswortZuruecksetzen />;
  }

  // Öffentliches Buchungs-Widget für Kunden
  if (pfad === '/buchen') {
    return (
      <div className="bg-slate-100 min-h-screen py-8">
        <BookingWidget />
      </div>
    );
  }

  // Spezielle Widget-Pfade VOR dem /widget/-Catch-All:
  // /widget/schulung → Schulungs-Anforderung-Form (statt Booking)
  if (pfad === '/widget/schulung') {
    return <SchulungAnfrageWidget />;
  }

  // Liane-Pflegebox-Bestell-Widget (kommt bei /liane-bestellung;
  // wird spaeter per iframe in die liane-pflegebox.de-Seite eingebettet).
  if (pfad === '/liane-bestellung' || pfad === '/widget/liane') {
    return <LianeBestellWidget />;
  }

  // Eingebettete Widget-Variante fuer externe Webseiten (iframe in Divi).
  // /widget/pd  → pflege-dschungel.de
  // /widget/spa → spa-pflegeberatung.de (kommt spaeter mit eigenen Farben)
  if (pfad.startsWith('/widget/')) {
    const marke = pfad.split('/')[2] || 'pd';
    return <EingebettetesWidget marke={marke} />;
  }

  // Feedback-Widget standalone (fuer Iframe-Embedding in app.pflege-dschungel.de).
  // Niko bindet diese URL als Iframe-src ein.
  if (pfad === '/feedback-widget') {
    return <FeedbackWidget />;
  }

  // Demo-Seite zum lokalen Testen ohne Iframe-Setup.
  if (pfad === '/feedback-demo') {
    return <FeedbackDemo />;
  }

  // Öffentliche Bestätigungsseite nach dem Klick auf den Double-Opt-In-Link.
  // Muss vor dem Auth-Gate stehen — der Kunde ist nicht eingeloggt und
  // sollte es auch gar nicht sein.
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('status') === 'erfolgreich_bestaetigt') {
    return <TerminBestaetigtSeite />;
  }

  // --- Geschützte Routes: Login erzwingen ---

  if (!user) {
    return <Login />;
  }

  // Erfolgreich eingeloggt → volles Cockpit
  return <CockpitHauptmenue />;
}

// ==========================================================================
// TerminBestaetigtSeite — öffentliche Dankesseite für Kunden nach dem
// Klick auf den Double-Opt-In-Link. Wird in AppInhalt vor dem Auth-Gate
// gerendert, damit sie ohne Login erreichbar ist.
// ==========================================================================
function TerminBestaetigtSeite() {
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-8 font-sans">
      <div className="max-w-md w-full bg-white p-10 rounded-2xl shadow-2xl text-center border-t-8 border-green-500 animate-fade-in">
        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
        <h1 className="text-3xl font-bold text-slate-800 mb-4">Termin bestätigt!</h1>
        <p className="text-slate-600 mb-8 leading-relaxed">
          Vielen Dank — Ihr Termin ist verbindlich in unseren Kalender eingetragen.
          Sie erhalten in wenigen Augenblicken eine finale Bestätigungs-E-Mail mit
          allen Details.
        </p>
        <p className="text-sm text-slate-500">
          Dieses Fenster können Sie jetzt schließen.
        </p>
      </div>
    </div>
  );
}

// ==========================================================================
// CockpitHauptmenue — das "alte App" ab hier, nur dass es Logout kennt
// ==========================================================================
const CI = { brombeer: '#85366C' };

// Tab-Definition: einmalig hier, damit Reihenfolge + Icons + Sichtbarkeit
// an einer Stelle gepflegt sind. 'nurAdmin' filtert Admin-only-Tabs raus.
// Phase 4: Kunden-Widget und Magic-Link-Portal sind aus der Tab-Leiste raus
// (selten genutzt — jetzt im Settings-Dropdown), dafuer kommen Pflege-Cockpit
// und Liane Box als eigene Geschaeftsbereiche dazu.
const TABS = [
  { key: 'berater',        label: 'Berater-Dashboard', icon: LayoutDashboard, nurAdmin: false },
  { key: 'mail-center',    label: 'Mail-Center',       icon: Mail,            nurAdmin: false },
  { key: 'pflege-cockpit', label: 'Pflege-Cockpit',    icon: Activity,        nurAdmin: false },
  { key: 'liane-box',      label: 'Liane Box',         icon: Package,         nurAdmin: false },
  { key: 'admin',          label: 'Super-Admin',       icon: ShieldCheck,     nurAdmin: true  },
];

// ==========================================================================
// NavSuche — Kunden-Schnellsuche in der Top-Navigation.
// ==========================================================================
// Eigene Komponente auf Modulebene (stabile Identitaet) mit INTERNEM
// Suchbegriff-State — sonst wuerde jeder Tastendruck den Fokus verlieren,
// weil die Top-Nav bei jedem Parent-Render neu gerendert wird (gleicher
// Fallstrick wie NeueTourPopupForm, siehe CLAUDE.md). onSelect(kundeId)
// oeffnet die Akte in-App.
function NavSuche({ kunden, onSelect }) {
  const [term, setTerm] = useState('');
  const [offen, setOffen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOffen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = term.trim().toLowerCase();
  const treffer = useMemo(() => {
    if (q.length < 2 || !Array.isArray(kunden)) return [];
    return kunden
      .filter(k => {
        const name = `${k.firstName || ''} ${k.lastName || ''}`.toLowerCase();
        const nameRueck = `${k.lastName || ''} ${k.firstName || ''}`.toLowerCase();
        const ang = k.relative ? `${k.relative.firstName || ''} ${k.relative.lastName || ''}`.toLowerCase() : '';
        return name.includes(q) || nameRueck.includes(q)
          || (k.zip || '').toLowerCase().includes(q)
          || (k.city || '').toLowerCase().includes(q)
          || ang.includes(q)
          || telefonTrifft(k, q);
      })
      .slice(0, 12);
  }, [q, kunden]);

  function waehle(k) {
    onSelect(k.id);
    setTerm('');
    setOffen(false);
  }

  return (
    <div className="relative flex-shrink-0" ref={boxRef} style={{ width: 220 }}>
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-100 border border-gray-200 focus-within:border-gray-300">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          type="text"
          value={term}
          onChange={e => { setTerm(e.target.value); setOffen(true); }}
          onFocus={() => setOffen(true)}
          placeholder="Kunde suchen…"
          className="bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none w-full"
        />
        {term && (
          <button
            onClick={() => { setTerm(''); setOffen(false); }}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            title="Leeren"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {offen && q.length >= 2 && (
        <div className="absolute left-0 top-full mt-1 w-80 max-w-[80vw] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[70] max-h-96 overflow-y-auto">
          {treffer.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">Keine Treffer für „{term}".</div>
          ) : (
            treffer.map(k => (
              <button
                key={k.id}
                onClick={() => waehle(k)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="text-sm font-bold text-gray-800">
                  {(k.lastName || '') + ', ' + (k.firstName || '')}
                </div>
                <div className="text-xs text-gray-500">
                  {[k.zip, k.city].filter(Boolean).join(' ') || 'Ohne Adresse'}
                  {k.relative && (k.relative.firstName || k.relative.lastName)
                    ? ` · Angeh.: ${k.relative.firstName || ''} ${k.relative.lastName || ''}`.trimEnd()
                    : ''}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CockpitHauptmenue() {
  const { user, logout, refreshUser } = useAuth();

  // --- Ansicht + offener Kunde aus der URL wiederherstellen ---
  // Damit ein Reload (oder ein geteilter Link / neuer Tab) genau dort landet,
  // wo man war, statt bei Home. Schema: ?view=<bereich>&kunde=<id>.
  // 'home' braucht keinen Param (saubere URL "/").
  const _startParams = new URLSearchParams(window.location.search);
  const _startView = _startParams.get('view');
  const _startKunde = _startParams.get('kunde');
  const _startFeedback = _startParams.get('feedback');
  const _initialView = _startView
    || (_startKunde ? 'berater' : (_startFeedback ? 'pflege-cockpit' : 'home'));

  // Startseite (Home) ist der Default nach dem Login. Tabs sind separate
  // Arbeits-Bereiche — "berater" ist die Akten-Sicht. 'home' ist KEIN Tab
  // (in TABS nicht enthalten), sondern wird ueber den Logo-/Titel-Klick
  // links oben erreicht.
  const [currentView, setCurrentView] = useState(_initialView);
  const [tourplanInitialId, setTourplanInitialId] = useState(null);

  // Wenn aus der Home-Seite ein Kunde angeklickt wird, soll die Berater-
  // Akte den Kunden direkt aufschlagen. Die kundeId reichen wir hier durch
  // — Berater_Dashboard liest sie ueber den Prop und resettet anschliessend.
  const [pendingKundeId, setPendingKundeId] = useState(_startKunde || null);
  const [pendingKundeTab, setPendingKundeTab] = useState(null);
  // Aktuell in der Akte geoeffneter Kunde — vom Berater-Dashboard nach oben
  // gemeldet, damit die URL ihn fuehren kann (Reload-Persistenz).
  const [aktiverKunde, setAktiverKunde] = useState(_startKunde || null);

  // Kundenliste fuer die Top-Nav-Schnellsuche. Einmal beim Mount geladen —
  // dieselbe Liste, die auch Home/Berater-Dashboard clientseitig durchsuchen.
  const [alleKundenNav, setAlleKundenNav] = useState([]);
  useEffect(() => {
    api.get('/api/kunden')
      .then(liste => setAlleKundenNav(Array.isArray(liste) ? liste : []))
      .catch(e => console.warn('NavSuche: Kundenliste konnte nicht geladen werden:', e));
  }, []);

  const handleOpenKundeFromHome = (kundeId) => {
    setPendingKundeId(kundeId);
    setPendingKundeTab(null);
    setCurrentView('berater');
  };

  // Sprung aus dem Mail-Center: oeffnet die Akte und stellt direkt den
  // Mail-Verlauf-Tab in der Akte ein, damit der Berater den Kontext nicht
  // verliert.
  const handleOpenKundeAusMailCenter = (kundeId, tab) => {
    setPendingKundeId(kundeId);
    setPendingKundeTab(tab || 'mail');
    setCurrentView('berater');
  };

  const consumePendingKunde = () => {
    setPendingKundeId(null);
    setPendingKundeTab(null);
  };

  // Sprung von Home in eine konkrete Anfrage: wir nutzen den vorhandenen
  // ?feedback=<id>-Tieflink-Mechanismus von Pflege_Cockpit.jsx — beim
  // Mount der Seite wird der Param gelesen und die Anfrage direkt selektiert.
  const handleOpenAnfrageFromHome = (anfrageId) => {
    if (anfrageId) {
      window.history.replaceState({}, '', `?feedback=${encodeURIComponent(anfrageId)}`);
    }
    setCurrentView('pflege-cockpit');
  };

  // Top-Nav-Badge: ungelesene Anfragen-Antworten. Wird beim Mount + bei
  // jedem View-Wechsel neu geladen, damit der Zaehler aktuell ist sobald
  // der Berater den Pflege-Cockpit-Tab wieder verlaesst und zurueckkommt.
  // Plus alle 60s Polling, damit auch ohne Tab-Wechsel neue IMAP-Antworten
  // sichtbar werden.
  const [feedbackStats, setFeedbackStats] = useState({ ungelesen_anfragen: 0, neu_7tage: 0 });
  const ladeFeedbackStats = async () => {
    try {
      const s = await api.get('/api/feedback/stats');
      setFeedbackStats(s || { ungelesen_anfragen: 0, neu_7tage: 0 });
    } catch (e) {
      // Badge ist UX-Komfort, kein Funktionsblocker — wir loggen aber
      // mindestens auf der Konsole, damit ein Endpoint-Bug nicht stillsxchweigend
      // verschluckt wird.
      console.warn('feedback/stats konnte nicht geladen werden:', e);
    }
  };

  // Top-Nav-Badge fuer Mail-Center: ungelesene eingehende Kunden-Mails.
  // Zaehlt analog zu feedbackStats — beim View-Wechsel + alle 60s.
  const [mailStats, setMailStats] = useState({ ungelesen: 0, nicht_zugeordnet: 0 });
  const ladeMailStats = async () => {
    try {
      const s = await api.get('/api/kundenmail/stats');
      setMailStats(s || { ungelesen: 0, nicht_zugeordnet: 0 });
    } catch (e) {
      console.warn('kundenmail/stats konnte nicht geladen werden:', e);
    }
  };

  useEffect(() => { ladeFeedbackStats(); ladeMailStats(); }, [currentView]);

  // Deep-Link "Tour öffnen" aus der Kundenakte: in den Tourplan wechseln
  // und die uebergebene Tour direkt oeffnen.
  useEffect(() => {
    const handler = (e) => {
      setTourplanInitialId(e.detail?.tourId || null);
      setCurrentView('tourplan');
    };
    window.addEventListener('tourplan:open', handler);
    return () => window.removeEventListener('tourplan:open', handler);
  }, []);

  // Vom Berater-Dashboard gemeldeter offener Kunde (oder null = Pipeline).
  const handleKundeChange = (kundeId) => setAktiverKunde(kundeId || null);

  // URL immer aktuell halten: ?view=<bereich>&kunde=<id>. So bringt ein
  // Reload einen genau dorthin zurueck, wo er war (frueher landete man
  // immer wieder auf Home und musste sich neu durchklicken). Andere Params
  // (z.B. ?feedback=) bleiben erhalten. replaceState statt pushState —
  // wir wollen Reload-Persistenz, ohne die Browser-Historie vollzuschreiben.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (currentView && currentView !== 'home') params.set('view', currentView);
    else params.delete('view');
    if (currentView === 'berater' && aktiverKunde) params.set('kunde', aktiverKunde);
    else params.delete('kunde');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [currentView, aktiverKunde]);

  // Browser Zurueck/Vor: Ansicht + Kunde aus der URL uebernehmen.
  useEffect(() => {
    const onPop = () => {
      const p = new URLSearchParams(window.location.search);
      const v = p.get('view');
      const k = p.get('kunde');
      setCurrentView(v || (k ? 'berater' : 'home'));
      if (k) { setPendingKundeId(k); setAktiverKunde(k); }
      else { setAktiverKunde(null); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    const id = setInterval(() => { ladeFeedbackStats(); ladeMailStats(); }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Settings-Menü in der Top-Navigation (selten genutzte Aktionen, z.B. Import).
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [show2fa, setShow2fa] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const settingsRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Persistente Top-Leiste: Logo + Titel links, Tabs in der Mitte,
  // User-Pille rechts (Settings + Logout). Ist auf jeder Seite gleich
  // und ersetzt den frueheren Hauptmenue-Bildschirm + die Floating-
  // Hauptmenue-Buttons in den einzelnen Views.
  function TopNavigation() {
    return (
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-[55]">
        <div className="px-6 py-2.5 flex items-center gap-4">
          {/* Logo + Titel — klickbar als Home-Button */}
          <button
            onClick={() => setCurrentView('home')}
            className="flex items-center gap-2 flex-shrink-0 hover:opacity-80 transition-opacity"
            title="Zur Startseite"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: CI.brombeer }}>P</div>
            <span className="font-bold text-lg text-gray-800 tracking-tight whitespace-nowrap">Beratungs-Cockpit</span>
          </button>

          {/* Kunden-Schnellsuche */}
          <NavSuche kunden={alleKundenNav} onSelect={handleOpenKundeFromHome} />

          {/* Tabs */}
          {/* Auf schmalen Bildschirmen scrollt die Reiterleiste in sich
              selbst, statt die ganze Seite breitzuziehen. Sonst rutscht
              beim Sprung aus der Aufgaben-App (?kunde=...) auf dem iPhone
              der gesamte Inhalt seitlich weg. Auf dem Mac unveraendert. */}
          <nav className="flex items-center gap-1 ml-2 min-w-0 overflow-x-auto">
            {TABS.filter(t => !t.nurAdmin || user.rolle === 'admin').map(t => {
              const Icon = t.icon;
              const aktiv = currentView === t.key;
              const badgeCount =
                t.key === 'pflege-cockpit' ? (feedbackStats?.ungelesen_anfragen || 0) :
                t.key === 'mail-center'   ? (mailStats?.ungelesen || 0) :
                0;
              return (
                <button
                  key={t.key}
                  onClick={() => setCurrentView(t.key)}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-bold transition-colors ${aktiv
                    ? 'text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'}`}
                  style={aktiv ? { backgroundColor: CI.brombeer } : {}}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  {badgeCount > 0 && (
                    <span
                      className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white bg-red-500 animate-pulse"
                      title={
                        t.key === 'mail-center'
                          ? `${badgeCount} ungelesene Kunden-Mail${badgeCount === 1 ? '' : 'n'}`
                          : `${badgeCount} Anfrage${badgeCount === 1 ? '' : 'n'} mit ungelesener Kunden-Antwort`
                      }
                    >
                      {badgeCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* User-Pille rechts */}
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs text-right">
              <div className="text-gray-500">Angemeldet als</div>
              <div className="font-bold text-gray-800">{user.name}</div>
            </div>
            <div className="relative" ref={settingsRef}>
              <button
                onClick={() => setSettingsOpen(o => !o)}
                className="flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full w-8 h-8 transition-colors"
                title="Einstellungen"
                aria-label="Einstellungen öffnen"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {settingsOpen && (
                <div className="absolute right-0 top-full mt-2 w-60 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[70]">
                  <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50">Einstellungen</div>
                  <button
                    onClick={() => { setShow2fa(true); setSettingsOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" style={{ color: user.twofa_aktiviert ? '#059669' : '#9ca3af' }} />
                    <span>Zwei-Faktor-Anmeldung{user.twofa_aktiviert ? ' (aktiv)' : ''}</span>
                  </button>
                  <button
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('kalender:open'));
                      setSettingsOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <CalendarIcon className="w-4 h-4 text-gray-500" />
                    <span>Kalender-Überblick öffnen</span>
                  </button>
                  <button
                    onClick={() => { setTourplanInitialId(null); setCurrentView('tourplan'); setSettingsOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span>Tourplan öffnen</span>
                  </button>
                  <button
                    onClick={() => { setShowImportModal(true); setSettingsOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <UploadCloud className="w-4 h-4 text-gray-500" />
                    <span>Kunden importieren (CSV)</span>
                  </button>
                  <button
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('verfuegbarkeit:open'));
                      setSettingsOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span>Verfügbarkeit pflegen</span>
                  </button>
                  <button
                    onClick={() => { setCurrentView('widget'); setSettingsOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span>Kunden-Widget Vorschau</span>
                  </button>
                  <button
                    onClick={() => { setCurrentView('portal'); setSettingsOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <LinkIcon className="w-4 h-4 text-gray-500" />
                    <span>Magic-Link Vorbereitung</span>
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1 text-xs font-bold text-gray-600 hover:text-red-600 bg-gray-100 hover:bg-red-50 px-3 py-1.5 rounded-full transition-colors"
              title="Abmelden"
            >
              <LogOut className="w-3.5 h-3.5" />
              Abmelden
            </button>
          </div>
        </div>
      </header>
    );
  }

  // Globales Import-Modal — wird aus dem Settings-Menü geöffnet.
  function ImportModal() {
    if (!showImportModal) return null;
    return (
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><UploadCloud className="w-5 h-5" style={{ color: CI.brombeer }} />Kunden importieren</h2>
            <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg"><X className="w-5 h-5"/></button>
          </div>
          <div className="p-8 text-center">
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer group">
              <UploadCloud className="w-12 h-12 text-gray-400 group-hover:text-blue-500 mx-auto mb-4 transition-colors" />
              <p className="font-bold text-gray-700 mb-1">Excel- oder CSV-Datei hier ablegen</p>
              <p className="text-xs text-gray-500">oder klicken, um eine Datei auszuwählen</p>
            </div>
            <p className="text-xs text-gray-400 mt-4 text-left leading-relaxed"><span className="font-bold">So funktioniert's:</span> Das System erkennt Spalten wie Name, PLZ und Telefonnummer automatisch.</p>
          </div>
          <div className="p-5 border-t border-gray-100 bg-slate-50 flex justify-end">
            <button onClick={() => setShowImportModal(false)} className="px-5 py-2.5 rounded-lg font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50">Schließen</button>
          </div>
        </div>
      </div>
    );
  }

  // Aktuellen Inhaltsbereich rendern. Top-Nav + Import-Modal sind global,
  // deshalb hier nur das jeweilige Page-Component.
  const renderInhalt = () => {
    if (currentView === 'home')           return <Home onOpenKunde={handleOpenKundeFromHome} onOpenAnfrage={handleOpenAnfrageFromHome} />;
    if (currentView === 'widget')         return <div className="bg-slate-100 min-h-screen py-8"><BookingWidget /></div>;
    if (currentView === 'portal')         return <div className="bg-slate-100 min-h-screen py-8"><VorbereitungsPortal /></div>;
    if (currentView === 'pflege-cockpit') return <PflegeCockpit />;
    if (currentView === 'liane-box')      return <LianeBox />;
    if (currentView === 'mail-center')    return <MailCenter onOpenKundeAkte={handleOpenKundeAusMailCenter} />;
    if (currentView === 'tourplan')       return <Tourplan initialTourId={tourplanInitialId} />;
    if (currentView === 'admin') {
      if (user.rolle !== 'admin') {
        return (
          <div className="min-h-[calc(100vh-60px)] bg-slate-100 flex items-center justify-center p-8">
            <div className="max-w-md bg-white p-8 rounded-2xl shadow text-center">
              <h2 className="text-xl font-bold text-red-600 mb-3">Kein Zugriff</h2>
              <p className="text-slate-600 mb-6">
                Nur Benutzer mit der Rolle „admin" dürfen die Super-Admin-Zentrale öffnen.
              </p>
              <button
                onClick={() => setCurrentView('home')}
                className="py-2 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold"
              >
                Zurück zur Startseite
              </button>
            </div>
          </div>
        );
      }
      return <SuperAdmin />;
    }
    // 'berater' (Default-Tab)
    return (
      <BeraterDashboard
        pendingKundeId={pendingKundeId}
        pendingTab={pendingKundeTab}
        onPendingKundeConsumed={consumePendingKunde}
        onKundeChange={handleKundeChange}
      />
    );
  };

  return (
    <div>
      {/* Als Funktionsaufruf (nicht <TopNavigation/>), damit die Nav nicht
          bei jedem Parent-Render als neuer Komponententyp neu gemountet wird
          — sonst verliert das Suchfeld darin bei jedem Tastendruck den Fokus. */}
      {TopNavigation()}
      <ImportModal />
      <VerfuegbarkeitsModal />
      {show2fa && (
        <TwoFaEinrichtung
          user={user}
          onClose={() => setShow2fa(false)}
          onChanged={refreshUser}
        />
      )}
      {renderInhalt()}
    </div>
  );
}


// ==========================================================================
//  VerfuegbarkeitsModal — Wochenplan + Abwesenheiten pflegen
//  Hoert auf das CustomEvent 'verfuegbarkeit:open' aus dem Settings-Menue.
// ==========================================================================
const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function VerfuegbarkeitsModal() {
  const [offen, setOffen] = useState(false);
  const [tab, setTab] = useState('woche');
  const [bloecke, setBloecke] = useState([]);
  const [abwesenheiten, setAbwesenheiten] = useState([]);
  const [privBloecke, setPrivBloecke] = useState([]);
  const [loading, setLoading] = useState(false);
  const [speichernState, setSpeichernState] = useState('');
  const [fehler, setFehler] = useState(null);

  const laden = async () => {
    setLoading(true); setFehler(null);
    try {
      const res = await fetch('/api/verfuegbarkeit/me', { credentials: 'include' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      setBloecke(data.wochenplan || []);
      setAbwesenheiten((data.abwesenheiten || []).map(a => ({
        von_datum: a.von_datum, bis_datum: a.bis_datum, notiz: a.notiz || '',
      })));
    } catch (e) {
      setFehler('Konnte Verfügbarkeit nicht laden.');
    } finally {
      setLoading(false);
    }
  };

  const ladePrivBloecke = async () => {
    try {
      const r = await fetch('/api/verfuegbarkeit/blockierungen', { credentials: 'include' });
      const d = await r.json();
      setPrivBloecke(Array.isArray(d) ? d : []);
    } catch { /* egal — Raster zeigt dann nur die Verfuegbarkeit */ }
  };

  useEffect(() => {
    const handler = () => { setOffen(true); laden(); ladePrivBloecke(); };
    window.addEventListener('verfuegbarkeit:open', handler);
    return () => window.removeEventListener('verfuegbarkeit:open', handler);
  }, []);

  // Beim Wechsel aufs Raster die privaten Sperren frisch holen (koennten im
  // Reiter "Private Sperren" gerade geaendert worden sein).
  useEffect(() => { if (offen && tab === 'raster') ladePrivBloecke(); }, [tab, offen]);

  const speichern = async () => {
    setSpeichernState('saving'); setFehler(null);
    try {
      const res = await fetch('/api/verfuegbarkeit/me', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wochenplan: bloecke,
          abwesenheiten: abwesenheiten.map(a => ({
            von_datum: a.von_datum, bis_datum: a.bis_datum,
            notiz: a.notiz || null,
          })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setSpeichernState('done');
      setTimeout(() => setSpeichernState(''), 1500);
    } catch (e) {
      setFehler(typeof e.message === 'string' ? e.message : 'Speichern fehlgeschlagen.');
      setSpeichernState('');
    }
  };

  const blockHinzufuegen = (wochentag) => {
    setBloecke(prev => [...prev, { wochentag, von: '09:00', bis: '12:00' }]);
  };
  const blockAendern = (idx, feld, wert) => {
    setBloecke(prev => prev.map((b, i) => i === idx ? { ...b, [feld]: wert } : b));
  };
  const blockLoeschen = (idx) => {
    setBloecke(prev => prev.filter((_, i) => i !== idx));
  };

  const abwesHinzufuegen = () => {
    const heute = new Date().toISOString().slice(0, 10);
    setAbwesenheiten(prev => [...prev, { von_datum: heute, bis_datum: heute, notiz: '' }]);
  };
  const abwesAendern = (idx, feld, wert) => {
    setAbwesenheiten(prev => prev.map((a, i) => i === idx ? { ...a, [feld]: wert } : a));
  };
  const abwesLoeschen = (idx) => {
    setAbwesenheiten(prev => prev.filter((_, i) => i !== idx));
  };

  if (!offen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-slate-50">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Clock className="w-5 h-5" style={{ color: CI.brombeer }} /> Verfügbarkeit pflegen
          </h2>
          <button onClick={() => setOffen(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 px-5">
          <button
            onClick={() => setTab('raster')}
            className={`px-4 py-3 text-sm font-bold border-b-2 flex items-center gap-1.5 ${tab === 'raster' ? 'text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={{ borderColor: tab === 'raster' ? CI.brombeer : 'transparent' }}
          ><Grid3x3 className="w-4 h-4" /> Wochenraster</button>
          <button
            onClick={() => setTab('woche')}
            className={`px-4 py-3 text-sm font-bold border-b-2 ${tab === 'woche' ? 'text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={{ borderColor: tab === 'woche' ? CI.brombeer : 'transparent' }}
          >Wochenplan (Liste)</button>
          <button
            onClick={() => setTab('privat')}
            className={`px-4 py-3 text-sm font-bold border-b-2 flex items-center gap-1.5 ${tab === 'privat' ? 'text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={{ borderColor: tab === 'privat' ? CI.brombeer : 'transparent' }}
          ><Ban className="w-4 h-4" /> Private Sperren</button>
          <button
            onClick={() => setTab('abwes')}
            className={`px-4 py-3 text-sm font-bold border-b-2 ${tab === 'abwes' ? 'text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={{ borderColor: tab === 'abwes' ? CI.brombeer : 'transparent' }}
          >Urlaub & Abwesenheit</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Plan…
            </div>
          ) : tab === 'raster' ? (
            <WochenRasterTab bloecke={bloecke} setBloecke={setBloecke} privBloecke={privBloecke} />
          ) : tab === 'woche' ? (
            <WochenplanTab
              bloecke={bloecke}
              onAdd={blockHinzufuegen}
              onChange={blockAendern}
              onDelete={blockLoeschen}
            />
          ) : tab === 'privat' ? (
            <PrivatSperrenTab />
          ) : (
            <AbwesenheitTab
              abwesenheiten={abwesenheiten}
              onAdd={abwesHinzufuegen}
              onChange={abwesAendern}
              onDelete={abwesLoeschen}
            />
          )}
        </div>

        <div className="p-5 border-t border-gray-100 bg-slate-50 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            {fehler ? <span className="text-red-600">{fehler}</span> :
             speichernState === 'saving' ? 'Speichere…' :
             speichernState === 'done' ? <span className="text-green-700 font-bold">✓ Gespeichert</span> :
             'Änderungen wirken sofort fürs Booking-Widget.'}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setOffen(false)} className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50">Schließen</button>
            <button
              onClick={speichern}
              disabled={speichernState === 'saving'}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white shadow-sm disabled:opacity-50"
              style={{ backgroundColor: CI.brombeer }}
            >Speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WochenplanTab({ bloecke, onAdd, onChange, onDelete }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500 mb-2">
        Pro Wochentag eine oder mehrere Zeiten anbieten — z.B. 09:00–12:00 + 14:00–18:00.
        Aus den Blöcken werden im Buchungs-Widget automatisch 30-Minuten-Slots.
        Tage ohne Block sind „nicht verfügbar".
      </div>
      {WOCHENTAGE.map((label, wochentag) => {
        const tagesBloecke = bloecke
          .map((b, i) => ({ ...b, _idx: i }))
          .filter(b => b.wochentag === wochentag);
        return (
          <div key={wochentag} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm text-gray-800">{label}</span>
              <button
                onClick={() => onAdd(wochentag)}
                className="text-xs font-bold text-fuchsia-700 hover:text-fuchsia-900"
              >+ Block hinzufügen</button>
            </div>
            {tagesBloecke.length === 0 ? (
              <div className="text-xs text-gray-400 italic">Kein Block — nicht verfügbar</div>
            ) : (
              <ul className="space-y-2">
                {tagesBloecke.map(b => (
                  <li key={b._idx} className="flex items-center gap-2 text-sm">
                    <input
                      type="time"
                      value={b.von}
                      onChange={(e) => onChange(b._idx, 'von', e.target.value)}
                      step="1800"
                      className="px-2 py-1 border border-gray-300 rounded"
                    />
                    <span className="text-gray-400">bis</span>
                    <input
                      type="time"
                      value={b.bis}
                      onChange={(e) => onChange(b._idx, 'bis', e.target.value)}
                      step="1800"
                      className="px-2 py-1 border border-gray-300 rounded"
                    />
                    <button
                      onClick={() => onDelete(b._idx)}
                      className="ml-auto text-gray-400 hover:text-red-600"
                      title="Block löschen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AbwesenheitTab({ abwesenheiten, onAdd, onChange, onDelete }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-500">
          Datums-Bereiche (von/bis) für Urlaub, Fortbildung oder Krankheit.
          An diesen Tagen wird das Buchungs-Widget keine freien Slots anbieten.
        </div>
        <button
          onClick={onAdd}
          className="text-xs font-bold text-fuchsia-700 hover:text-fuchsia-900 whitespace-nowrap ml-2"
        >+ Neue Abwesenheit</button>
      </div>
      {abwesenheiten.length === 0 ? (
        <div className="text-sm text-gray-400 italic bg-gray-50 border border-gray-200 rounded p-4 text-center">
          Keine Abwesenheiten eingetragen.
        </div>
      ) : (
        <ul className="space-y-2">
          {abwesenheiten.map((a, idx) => (
            <li key={idx} className="border border-gray-200 rounded-lg p-3 flex items-center gap-2 flex-wrap">
              <input
                type="date" value={a.von_datum}
                onChange={(e) => onChange(idx, 'von_datum', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <span className="text-gray-400 text-sm">bis</span>
              <input
                type="date" value={a.bis_datum}
                onChange={(e) => onChange(idx, 'bis_datum', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <input
                type="text" value={a.notiz}
                onChange={(e) => onChange(idx, 'notiz', e.target.value)}
                placeholder="Notiz (z.B. Sommerurlaub)"
                className="flex-1 min-w-[150px] px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <button
                onClick={() => onDelete(idx)}
                className="text-gray-400 hover:text-red-600"
                title="Eintrag löschen"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ==========================================================================
//  WochenRasterTab — visuelles Raster (Mo-So x 30-Min) zum Freigeben/Sperren
//  der wiederkehrenden Verfuegbarkeit per Klick + Ziehen.
// ==========================================================================
const _RASTER_START_MIN = 7 * 60;
const _RASTER_ENDE_MIN = 21 * 60;
const _RASTER_ZEITEN = [];
for (let _m = _RASTER_START_MIN; _m < _RASTER_ENDE_MIN; _m += 30) _RASTER_ZEITEN.push(_m);
const _WT_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const _minZuHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const _hhmmZuMin = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + m; };

function _slotsAusBloecke(bloecke) {
  const s = new Set();
  (bloecke || []).forEach(b => {
    const v = _hhmmZuMin(b.von), e = _hhmmZuMin(b.bis);
    for (let m = v; m < e; m += 30) s.add(`${b.wochentag}-${m}`);
  });
  return s;
}
function _bloeckeAusSlots(set) {
  const perTag = {};
  set.forEach(k => { const [wt, m] = k.split('-').map(Number); (perTag[wt] = perTag[wt] || []).push(m); });
  const out = [];
  Object.entries(perTag).forEach(([wt, mins]) => {
    mins.sort((a, b) => a - b);
    let start = null, prev = null;
    mins.forEach(m => {
      if (start === null) { start = m; prev = m; }
      else if (m === prev + 30) { prev = m; }
      else { out.push({ wochentag: Number(wt), von: _minZuHHMM(start), bis: _minZuHHMM(prev + 30) }); start = m; prev = m; }
    });
    if (start !== null) out.push({ wochentag: Number(wt), von: _minZuHHMM(start), bis: _minZuHHMM(prev + 30) });
  });
  return out;
}

function WochenRasterTab({ bloecke, setBloecke, privBloecke }) {
  const slotSet = _slotsAusBloecke(bloecke);
  // Woechentliche private Sperren als Overlay (einmalige gehoeren nicht ins
  // Wochen-Template — die haengen an einem Datum).
  const privSet = new Set();
  (privBloecke || []).filter(b => b.typ === 'woechentlich' && b.wochentag != null).forEach(b => {
    const v = _hhmmZuMin(b.von), e = _hhmmZuMin(b.bis);
    for (let m = v; m < e; m += 30) privSet.add(`${b.wochentag}-${m}`);
  });
  const paintRef = useRef(null); // 'add' | 'remove' | null

  const setzeSlot = (wt, m, an) => {
    const neu = _slotsAusBloecke(bloecke);
    const key = `${wt}-${m}`;
    if (an) neu.add(key); else neu.delete(key);
    setBloecke(_bloeckeAusSlots(neu));
  };
  const onDown = (wt, m) => {
    const an = !slotSet.has(`${wt}-${m}`);
    paintRef.current = an ? 'add' : 'remove';
    setzeSlot(wt, m, an);
  };
  const onEnter = (wt, m) => { if (paintRef.current) setzeSlot(wt, m, paintRef.current === 'add'); };
  useEffect(() => {
    const up = () => { paintRef.current = null; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const tagToggle = (wt) => {
    const hat = _RASTER_ZEITEN.some(m => slotSet.has(`${wt}-${m}`));
    const neu = _slotsAusBloecke(bloecke);
    _RASTER_ZEITEN.forEach(m => neu.delete(`${wt}-${m}`));
    if (!hat) { for (let m = 8 * 60; m < 18 * 60; m += 30) neu.add(`${wt}-${m}`); }
    setBloecke(_bloeckeAusSlots(neu));
  };

  return (
    <div className="select-none">
      <div className="text-xs text-gray-500 mb-3">
        Klicke (oder ziehe) über die Felder, um deine <b>wiederkehrende</b> Verfügbarkeit zu setzen.
        Grün = verfügbar. Klick auf einen Wochentag-Kopf gibt den ganzen Tag frei (08–18 Uhr) bzw. leert ihn.
        Nicht vergessen: unten <b>Speichern</b>.
      </div>
      <div className="overflow-auto border border-gray-200 rounded-lg">
        <div className="grid" style={{ gridTemplateColumns: '52px repeat(7, minmax(48px, 1fr))', minWidth: 480 }}>
          <div className="bg-gray-50 border-b border-gray-200" />
          {_WT_KURZ.map((t, wt) => (
            <button key={wt} onClick={() => tagToggle(wt)}
              className="bg-gray-50 border-b border-l border-gray-200 py-1.5 text-xs font-bold text-gray-600 hover:bg-fuchsia-50"
              title="Ganzen Tag freigeben / leeren">{t}</button>
          ))}
          {_RASTER_ZEITEN.map(m => (
            <Fragment key={m}>
              <div className={`text-[10px] text-right pr-1 border-b border-gray-100 flex items-center justify-end ${m % 60 === 0 ? 'text-gray-600 font-semibold' : 'text-gray-300'}`} style={{ height: 20 }}>
                {m % 60 === 0 ? _minZuHHMM(m) : ''}
              </div>
              {_WT_KURZ.map((_, wt) => {
                const an = slotSet.has(`${wt}-${m}`);
                const privat = privSet.has(`${wt}-${m}`);
                const farbe = privat
                  ? 'bg-rose-300 hover:bg-rose-400'
                  : (an ? 'bg-emerald-400 hover:bg-emerald-500' : 'bg-white hover:bg-emerald-50');
                return (
                  <div
                    key={wt}
                    onMouseDown={() => onDown(wt, m)}
                    onMouseEnter={() => onEnter(wt, m)}
                    className={`border-b border-l border-gray-100 cursor-pointer ${farbe}`}
                    style={{ height: 20 }}
                    title={privat
                      ? `${_WT_KURZ[wt]} ${_minZuHHMM(m)} — privat gesperrt (Reiter „Private Sperren")`
                      : `${_WT_KURZ[wt]} ${_minZuHHMM(m)} — ${an ? 'verfügbar (klick zum Sperren)' : 'nicht freigegeben (klick zum Freigeben)'}`}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 flex-wrap">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-400" /> verfügbar</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-white border border-gray-300" /> nicht freigegeben</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-rose-300" /> privat gesperrt (wöchentlich)</span>
      </div>
    </div>
  );
}

// ==========================================================================
//  PrivatSperrenTab — feine private Zeit-Sperren (einmalig / woechentlich).
//  Speichert sofort ueber eigene Endpoints (unabhaengig vom Modal-Speichern).
// ==========================================================================
function PrivatSperrenTab() {
  const [liste, setListe] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const [form, setForm] = useState({
    typ: 'einmalig',
    datum: new Date().toISOString().slice(0, 10),
    wochentag: 0, von: '12:00', bis: '13:00', bezeichnung: '',
  });

  const laden = async () => {
    setLaedt(true); setFehler(null);
    try {
      const r = await fetch('/api/verfuegbarkeit/blockierungen', { credentials: 'include' });
      const d = await r.json();
      setListe(Array.isArray(d) ? d : []);
    } catch { setListe([]); } finally { setLaedt(false); }
  };
  useEffect(() => { laden(); }, []);

  const hinzufuegen = async () => {
    setFehler(null); setSpeichert(true);
    try {
      const body = {
        typ: form.typ, von: form.von, bis: form.bis,
        bezeichnung: form.bezeichnung || null,
        datum: form.typ === 'einmalig' ? form.datum : null,
        wochentag: form.typ === 'woechentlich' ? Number(form.wochentag) : null,
      };
      const r = await fetch('/api/verfuegbarkeit/blockierungen', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || `HTTP ${r.status}`); }
      setForm(f => ({ ...f, bezeichnung: '' }));
      await laden();
    } catch (e) { setFehler(typeof e.message === 'string' ? e.message : 'Speichern fehlgeschlagen.'); }
    finally { setSpeichert(false); }
  };
  const loeschen = async (id) => {
    if (!window.confirm('Diese private Sperre löschen?')) return;
    try { await fetch(`/api/verfuegbarkeit/blockierungen/${id}`, { method: 'DELETE', credentials: 'include' }); await laden(); }
    catch { /* egal */ }
  };

  const beschreibe = (b) => {
    const zeit = `${b.von}–${b.bis}`;
    if (b.typ === 'woechentlich') return `jeden ${WOCHENTAGE[b.wochentag] || '?'}, ${zeit}`;
    let d = b.datum;
    try { d = new Date(b.datum).toLocaleDateString('de-DE'); } catch { /* egal */ }
    return `${d}, ${zeit}`;
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500">
        Private Sperren blockieren einzelne Zeitfenster (z.B. Arzttermin, Mittagspause) — einmalig oder
        wöchentlich wiederkehrend. Sie werden <b>sofort</b> gespeichert. Nach außen (Buchungs-Widget) ist
        dann nur „nicht verfügbar" sichtbar, nie die Bezeichnung.
      </div>

      {/* Neue Sperre */}
      <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-1.5">
            <input type="radio" checked={form.typ === 'einmalig'} onChange={() => setForm(f => ({ ...f, typ: 'einmalig' }))} /> einmalig
          </label>
          <label className="flex items-center gap-1.5">
            <input type="radio" checked={form.typ === 'woechentlich'} onChange={() => setForm(f => ({ ...f, typ: 'woechentlich' }))} /> wöchentlich
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {form.typ === 'einmalig' ? (
            <input type="date" value={form.datum} onChange={e => setForm(f => ({ ...f, datum: e.target.value }))}
              className="px-2 py-1 border border-gray-300 rounded text-sm" />
          ) : (
            <select value={form.wochentag} onChange={e => setForm(f => ({ ...f, wochentag: e.target.value }))}
              className="px-2 py-1 border border-gray-300 rounded text-sm bg-white">
              {WOCHENTAGE.map((t, i) => <option key={i} value={i}>{t}</option>)}
            </select>
          )}
          <input type="time" value={form.von} onChange={e => setForm(f => ({ ...f, von: e.target.value }))}
            className="px-2 py-1 border border-gray-300 rounded text-sm" />
          <span className="text-gray-400 text-sm">bis</span>
          <input type="time" value={form.bis} onChange={e => setForm(f => ({ ...f, bis: e.target.value }))}
            className="px-2 py-1 border border-gray-300 rounded text-sm" />
          <input type="text" value={form.bezeichnung} onChange={e => setForm(f => ({ ...f, bezeichnung: e.target.value }))}
            placeholder="Bezeichnung (intern, optional)"
            className="flex-1 min-w-[140px] px-2 py-1 border border-gray-300 rounded text-sm" />
          <button onClick={hinzufuegen} disabled={speichert}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-sm font-bold disabled:opacity-50"
            style={{ backgroundColor: CI.brombeer }}>
            <Plus className="w-4 h-4" /> Sperre
          </button>
        </div>
        {fehler && <div className="text-xs text-red-600">{fehler}</div>}
      </div>

      {/* Liste */}
      {laedt ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
      ) : (liste && liste.length === 0) ? (
        <div className="text-sm text-gray-400 italic bg-gray-50 border border-gray-200 rounded p-4 text-center">Keine privaten Sperren eingetragen.</div>
      ) : (
        <ul className="space-y-2">
          {(liste || []).map(b => (
            <li key={b.id} className="border border-gray-200 rounded-lg p-3 flex items-center gap-3">
              <Ban className="w-4 h-4 text-gray-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800">{beschreibe(b)}</div>
                {b.bezeichnung && <div className="text-xs text-gray-500">{b.bezeichnung}</div>}
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${b.typ === 'woechentlich' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                {b.typ === 'woechentlich' ? 'wöchentlich' : 'einmalig'}
              </span>
              <button onClick={() => loeschen(b.id)} className="text-gray-400 hover:text-red-600" title="Sperre löschen">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
